/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useState } from 'react';
import { Modal } from '../../../../shared/modals/Modal';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { plural } from '../../../../shared/format/plural';
import { ticketChrome } from '../../../../shared/ticket/ticketChrome.styles'
import { ticketColors } from '../../../../shared/theme/ticketColors'
import { TicketButton } from '../../../../shared/ticket/TicketButton';
import { spacing } from '../../../../shared/theme/spacing'
import { typography } from '../../../../shared/theme/typography';
import type { PermissionLevel } from '../../../store/useEmployeesStore';
import type { ModuleKey } from '../../../../shell/session/store/sessionTypes';
import { colors } from '../../../../shared/theme/colors';
import { useOperationalConfirm } from '../../../../shared/ui/useOperationalConfirm';
import { getInitials } from '../../../teamUtils';
import type { EmployeePermissionsVm } from './useEmployeePermissions';
import { resolveBranchDisplayName } from './useEmployeePermissions';
import { ROLE_PRESETS, filterPresetsByCapabilities, invitationRoleLabel } from './EmployeeRolePresets';
import { useEffectiveBusinessCapabilities } from '../../../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { buildEffectivePermissionSummary } from './permissionUiLabels';
import { RolePresetPicker } from './RolePresetPicker';
import { EmployeePermissionCopyPanel } from './EmployeePermissionCopyPanel';
import { EmployeePermissionModuleRow } from './EmployeePermissionModuleRow';
import { EmployeeCommissionSection } from './EmployeeCommissionSection';
import {
  filterPermissionsByModuleQuery,
  groupPermissionsByZone,
} from './moduleNavZones';

type Props = {
  vm: EmployeePermissionsVm;
};

export function EmployeeDetailModal({ vm }: Props) {
  const insets = useSafeAreaInsets();
  const { height: screenHeight, width: screenWidth } = useWindowDimensions();
  const wide = screenWidth >= 768;

  const employee = vm.selectedEmployee;
  const [selectedBranchIdx, setSelectedBranchIdx] = useState(0);
  const [branchDropdownOpen, setBranchDropdownOpen] = useState(false);
  const [openModule, setOpenModule] = useState<string | null>(null);
  const [customizeModule, setCustomizeModule] = useState<string | null>(null);
  const [moduleFilter, setModuleFilter] = useState('');
  const effectiveCapabilities = useEffectiveBusinessCapabilities();
  const { confirm, ConfirmModal } = useOperationalConfirm();
  const visiblePresets = useMemo(
    () => filterPresetsByCapabilities(ROLE_PRESETS, effectiveCapabilities),
    [effectiveCapabilities],
  );

  const {
    branches,
    branchNameById,
    visibleModules,
    busyKey,
    loadEmployees,
    onAddBranch,
    onRemoveBranch,
    onRevoke,
    onCloseDetail,
    setPermissionLevel,
    setPermissionAction,
    toggleAgendaManagement,
    savePendingPermissions,
    hasPendingChanges,
    pendingChangesCount,
    savingPermissions,
    onDiscardChanges,
    onApplyPreset,
    onCopyFromBranch,
    onCopyFromEmployee,
    filteredActiveEmployees,
    canEditPermissions,
    canAssignBranches,
    canRevoke,
  } = vm;

  const assignments = employee?.branchAssignments ?? [];
  const safeIdx = Math.min(selectedBranchIdx, Math.max(assignments.length - 1, 0));
  const activeAssignment = assignments[safeIdx];
  const activeBranchId = activeAssignment?.branchId ?? '';

  const assignedIds = new Set(assignments.map((a) => a.branchId));
  const branchesToAdd = branches.filter((b) => !assignedIds.has(b.id));
  const canRemoveBranch = assignments.length > 1;
  const assignmentCount = assignments.length;
  const assignmentCountLabel =
    assignmentCount === 1 ? '1 sucursal' : `${assignmentCount} sucursales`;

  const visiblePerms = useMemo(() => {
    if (!activeAssignment) return [];
    const byModule = new Map(activeAssignment.permissions.map((p) => [p.module, p]));
    return visibleModules.map((module) => {
      const existing = byModule.get(module);
      if (existing) return existing;
      return {
        module,
        canView: false,
        canEdit: false,
        canVoid: false,
        canDiscount: module === 'POS' ? false : undefined,
        canManageAgenda: module === 'Agenda' ? false : undefined,
        canViewOthers: true,
        actions: {},
      };
    });
  }, [activeAssignment, visibleModules]);

  const summary = useMemo(
    () => buildEffectivePermissionSummary(visiblePerms, visibleModules),
    [visiblePerms, visibleModules]
  );

  const filteredPerms = useMemo(
    () => filterPermissionsByModuleQuery(visiblePerms, moduleFilter),
    [visiblePerms, moduleFilter]
  );

  const permissionGroups = useMemo(
    () => groupPermissionsByZone(filteredPerms, visibleModules),
    [filteredPerms, visibleModules]
  );

  const sheetMaxHeight = screenHeight * (wide ? 0.88 : 0.92);

  if (!employee) return null;

  // La membresía owner (role='owner') no es un empleado gestionable: no tiene
  // EBP, no cobra comisión propia y no puede revocarse. Vista read-only.
  const isOwnerRow = employee.isOwner === true;

  const handleSelectLevel = (module: string, level: PermissionLevel) => {
    setPermissionLevel(employee.id, activeBranchId, module as ModuleKey, level);
    setOpenModule(null);
  };

  const handleSave = async () => {
    const ok = await savePendingPermissions();
    if (ok) {
      setOpenModule(null);
      setCustomizeModule(null);
      setBranchDropdownOpen(false);
      onCloseDetail();
    }
  };

  const handleClose = async () => {
    if (savingPermissions) return;
    if (hasPendingChanges && !savingPermissions) {
      const ok = await confirm({
        title: 'Cambios sin guardar',
        message: `Tenés ${pendingChangesCount} ${plural(pendingChangesCount, 'cambio', 'cambios')} sin guardar. ¿Cerrar sin guardar?`,
        confirmLabel: 'Cerrar sin guardar',
        destructive: true,
      });
      if (!ok) return;
      onDiscardChanges();
      setOpenModule(null);
      setCustomizeModule(null);
      setBranchDropdownOpen(false);
      onCloseDetail();
      return;
    }
    setOpenModule(null);
    setCustomizeModule(null);
    setBranchDropdownOpen(false);
    onCloseDetail();
  };

  return (
    <>
    <Modal
      visible
      transparent
      animationType={wide ? 'fade' : 'slide'}
      onRequestClose={handleClose}
      accessibilityViewIsModal
    >
      <View
        style={[
          ticketChrome.sheetBackdrop,
          wide && styles.backdropWide,
        ]}
      >
        <Pressable style={StyleSheet.absoluteFill} onPress={handleClose} />
        <View
          style={[
            wide ? ticketChrome.dialogCard : ticketChrome.sheetCard,
            {
              paddingBottom: Math.max(insets.bottom, spacing.sm),
              maxHeight: sheetMaxHeight,
              width: wide ? Math.min(screenWidth - 48, 720) : '100%',
            },
          ]}
        >
          {!wide ? <View style={ticketChrome.grabber} /> : null}
          <View style={ticketChrome.sheetHeader}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={ticketChrome.sheetTitle} testID="perfil.team.detail.title">Permisos del empleado</Text>
              <Text style={ticketChrome.sheetSubtitle} numberOfLines={1}>
                {employee.name}
              </Text>
            </View>
            <TouchableOpacity
              style={ticketChrome.sheetCloseBtn}
              onPress={handleClose}
              accessibilityRole="button"
              accessibilityLabel="Cerrar"
            >
              <Text style={ticketChrome.sheetCloseGlyph}>×</Text>
            </TouchableOpacity>
          </View>

          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.hero}>
              <View style={styles.heroAvatar}>
                <Text style={styles.heroAvatarText}>{getInitials(employee.name)}</Text>
              </View>
              <View style={styles.heroInfo}>
                <Text style={styles.heroName} numberOfLines={1}>
                  {employee.name}
                </Text>
                <Text style={styles.heroRole} numberOfLines={1}>
                  {invitationRoleLabel(employee.role)}
                </Text>
                <Text style={styles.heroMeta} numberOfLines={2}>
                  {assignmentCountLabel}
                  {employee.email ? `  ·  ${employee.email}` : ''}
                </Text>
              </View>
            </View>

            <View style={styles.summaryCard}>
              <Text style={styles.sectionLabel}>Qué puede hacer</Text>
              {isOwnerRow ? (
                <Text style={styles.summaryCan}>Es el dueño del negocio: tiene acceso a todo.</Text>
              ) : (
                <>
                  <Text style={styles.summaryCan}>{summary.can}</Text>
                  <Text style={styles.summaryCannot}>{summary.cannot}</Text>
                </>
              )}
            </View>

            {canEditPermissions && !isOwnerRow ? (
              <View style={styles.section}>
                <Text style={styles.sectionLabel} testID="perfil.team.detail.section.comisiones">Comisiones</Text>
                <EmployeeCommissionSection
                  authUserId={employee.authUserId ?? ''}
                  earnsCommission={employee.earnsCommission === true}
                  commissionPlanId={employee.commissionPlanId ?? null}
                  onChanged={() => void loadEmployees({ force: true })}
                />
              </View>
            ) : null}

            {!isOwnerRow ? (
            <View style={styles.section}>
              <Text style={styles.sectionLabel} testID="perfil.team.detail.section.sucursal">Sucursal</Text>
              <View style={styles.branchRow}>
                <View style={styles.branchDropdownGrow}>
                  <TouchableOpacity
                    style={styles.branchDropdown}
                    onPress={() => {
                      setBranchDropdownOpen((v) => !v);
                      setOpenModule(null);
                      setCustomizeModule(null);
                    }}
                    accessibilityRole="button"
                    accessibilityLabel="Seleccionar sucursal"
                    testID="perfil.team.detail.branch-dropdown"
                  >
                    <View style={styles.branchDropdownLeft}>
                      <Ionicons name="storefront-outline" size={14} color={ticketColors.ink3} />
                      <Text style={styles.branchDropdownLabel} numberOfLines={1}>
                        {resolveBranchDisplayName(branchNameById, activeBranchId)}
                      </Text>
                    </View>
                    <Ionicons
                      name={branchDropdownOpen ? 'chevron-up' : 'chevron-down'}
                      size={16}
                      color={ticketColors.ink3}
                    />
                  </TouchableOpacity>
                  {branchDropdownOpen ? (
                    <View style={styles.dropdownList}>
                      {assignments.map((assignment, i) => {
                        const isActive = i === safeIdx;
                        const name = resolveBranchDisplayName(
                          branchNameById,
                          assignment.branchId,
                          assignment.branchName ?? 'Sucursal'
                        );
                        return (
                          <TouchableOpacity
                            key={assignment.branchId}
                            style={[styles.dropdownItem, isActive && styles.dropdownItemActive]}
                            onPress={() => {
                              setSelectedBranchIdx(i);
                              setBranchDropdownOpen(false);
                              setOpenModule(null);
                              setCustomizeModule(null);
                            }}
                            accessibilityRole="button"
                            accessibilityLabel={`Seleccionar ${name}`}
                          >
                            <Text
                              style={[
                                styles.dropdownItemText,
                                isActive && styles.dropdownItemTextActive,
                              ]}
                              numberOfLines={1}
                            >
                              {name}
                            </Text>
                            {isActive ? (
                              <Ionicons name="checkmark" size={16} color={ticketColors.gold} />
                            ) : null}
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  ) : null}
                </View>
                {canAssignBranches && canRemoveBranch ? (
                  <TouchableOpacity
                    style={styles.removeBranchBtn}
                    onPress={() =>
                      onRemoveBranch(
                        employee.id,
                        activeBranchId,
                        employee.name,
                        canRemoveBranch
                      )
                    }
                    disabled={busyKey === `rm-${employee.id}-${activeBranchId}`}
                    accessibilityRole="button"
                    accessibilityLabel={`Quitar de ${resolveBranchDisplayName(branchNameById, activeBranchId, 'esta sucursal')}`}
                  >
                    <Ionicons name="remove-circle-outline" size={16} color={ticketColors.bad} />
                    <Text style={styles.removeBranchBtnLabel}>Quitar</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            </View>
            ) : null}

            {!isOwnerRow && canAssignBranches && branchesToAdd.length > 0 ? (
              <View style={styles.section}>
                <Text style={styles.sectionLabel} testID="perfil.team.detail.section.sin-acceso">Sumar sucursal</Text>
                <View style={styles.pendingChips}>
                  {branchesToAdd.map((b) => (
                    <TouchableOpacity
                      key={b.id}
                      style={styles.addBranchChip}
                      onPress={() => onAddBranch(employee.id, b.id, employee.name)}
                      disabled={busyKey === `add-${employee.id}-${b.id}`}
                      accessibilityRole="button"
                      accessibilityLabel={`Dar acceso a ${b.name}`}
                      testID="perfil.team.detail.add-branch"
                    >
                      <Ionicons name="add" size={12} color={ticketColors.gold} />
                      <Text style={styles.addBranchChipLabel}>{b.name}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            ) : !isOwnerRow && branchesToAdd.length === 0 && branches.length > 0 ? (
              <Text style={styles.allBranchesHint} testID="perfil.team.detail.all-branches">Tiene acceso a todas las sucursales.</Text>
            ) : null}

            {canEditPermissions && !isOwnerRow ? (
            <View style={styles.section}>
              <Text style={styles.sectionLabel} testID="perfil.team.detail.section.plantilla">Aplicar rol</Text>
              <Text style={styles.sectionHint}>
                Elegí un rol y después afiná módulo por módulo.
              </Text>
              <RolePresetPicker
                presets={visiblePresets}
                onApply={(preset) =>
                  onApplyPreset(employee.id, activeBranchId, employee.name, preset)
                }
              />
            </View>
            ) : null}

            {canEditPermissions && !isOwnerRow ? (
              <EmployeePermissionCopyPanel
                employee={employee}
                activeBranchId={activeBranchId}
                branchNameById={branchNameById}
                peerEmployees={filteredActiveEmployees}
                onCopyFromBranch={(sourceBranchId) =>
                  onCopyFromBranch(employee.id, activeBranchId, sourceBranchId, employee.name)
                }
                onCopyFromEmployee={(sourceEmployeeId) =>
                  onCopyFromEmployee(
                    employee.id,
                    activeBranchId,
                    sourceEmployeeId,
                    employee.name
                  )
                }
                canEdit={canEditPermissions}
              />
            ) : null}

            {!isOwnerRow ? (
            <View style={styles.section}>
              <Text style={styles.sectionLabel} testID="perfil.team.detail.section.permisos">Permisos por módulo</Text>
              <TextInput
                value={moduleFilter}
                onChangeText={setModuleFilter}
                placeholder="Filtrar módulo…"
                placeholderTextColor={colors.inputPlaceholder}
                style={styles.moduleFilterInput}
                accessibilityLabel="Filtrar módulo"
                testID="perfil.team.detail.module-filter"
              />
              <View style={styles.permList}>
                {permissionGroups.map((group) => (
                  <View key={group.zone} style={styles.zoneGroup}>
                    <Text style={styles.zoneLabel}>{group.label}</Text>
                    {group.permissions.map((perm, permIdx) => {
                      return (
                        <EmployeePermissionModuleRow
                          key={`${activeBranchId}-${perm.module}`}
                          perm={perm}
                          activeBranchId={activeBranchId}
                          isLast={permIdx === group.permissions.length - 1}
                          expanded={openModule === perm.module}
                          customizeOpen={customizeModule === perm.module}
                          canEdit={canEditPermissions}
                          onToggleExpand={() => {
                            setBranchDropdownOpen(false);
                            setCustomizeModule(null);
                            setOpenModule(openModule === perm.module ? null : perm.module);
                          }}
                          onToggleCustomize={() =>
                            setCustomizeModule(
                              customizeModule === perm.module ? null : perm.module
                            )
                          }
                          onSelectLevel={(level) => handleSelectLevel(perm.module, level)}
                          onToggleAgenda={(field) =>
                            toggleAgendaManagement(
                              employee.id,
                              activeBranchId,
                              'Agenda',
                              field
                            )
                          }
                          onSetAction={(action, enabled) =>
                            setPermissionAction(
                              employee.id,
                              activeBranchId,
                              perm.module as ModuleKey,
                              action,
                              enabled
                            )
                          }
                        />
                      );
                    })}
                  </View>
                ))}
                {permissionGroups.length === 0 ? (
                  <Text style={styles.sectionHint}>Ningún módulo coincide con el filtro.</Text>
                ) : null}
              </View>
            </View>
            ) : null}

            {canRevoke && !isOwnerRow ? (
            <TouchableOpacity
              style={styles.revokeFootBtn}
              onPress={() => {
                if (hasPendingChanges) onDiscardChanges();
                onCloseDetail();
                onRevoke(employee.id, employee.name);
              }}
              disabled={Boolean(busyKey?.startsWith(`revoke-${employee.id}`))}
              accessibilityRole="button"
              accessibilityLabel={`Revocar acceso de ${employee.name}`}
              testID="perfil.team.detail.revoke"
            >
              <Ionicons name="ban-outline" size={16} color={ticketColors.bad} />
              <Text style={styles.revokeFootBtnLabel}>Revocar acceso</Text>
            </TouchableOpacity>
            ) : null}
          </ScrollView>

          <View style={ticketChrome.foot}>
            {hasPendingChanges ? (
              <TicketButton
                label="Descartar"
                variant="ghost"
                onPress={onDiscardChanges}
                disabled={savingPermissions}
                style={styles.footBtn}
              />
            ) : null}
            <TicketButton
              label={
                savingPermissions
                  ? 'Guardando…'
                  : hasPendingChanges
                    ? `Guardar (${pendingChangesCount})`
                    : 'Guardar'
              }
              variant="gold"
              onPress={() => void handleSave()}
              disabled={!hasPendingChanges || savingPermissions}
              style={[styles.footBtn, styles.footBtnGrow]}
              testID="perfil.team.detail.save"
            />
          </View>
        </View>
      </View>
    </Modal>
    {ConfirmModal}
    </>
  );
}

const styles = StyleSheet.create({
  backdropWide: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  scroll: {
    flexGrow: 0,
    flexShrink: 1,
  },
  scrollContent: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.md,
    gap: spacing.md,
  },
  hero: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingBottom: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
  },
  heroAvatar: {
    width: 44,
    height: 44,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ticketColors.paper2,
    flexShrink: 0,
  },
  heroAvatarText: {
    fontSize: 16,
    fontWeight: '800',
    color: ticketColors.ink2,
  },
  heroInfo: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  heroName: {
    ...typography.title3,
    color: ticketColors.ink,
    fontWeight: '800',
  },
  heroRole: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
  heroMeta: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontSize: 12,
  },
  summaryCard: {
    gap: 4,
    padding: spacing.sm,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
  },
  summaryCan: {
    ...typography.caption,
    color: ticketColors.ink,
    fontSize: 13,
    lineHeight: 18,
  },
  summaryCannot: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontSize: 12,
    lineHeight: 17,
  },
  section: {
    gap: spacing.xs,
  },
  sectionLabel: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '700',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.35,
  },
  sectionHint: {
    ...typography.caption,
    color: ticketColors.ink3,
    marginBottom: 2,
  },
  moduleFilterInput: {
    ...typography.body,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    color: ticketColors.ink,
    backgroundColor: ticketColors.paper,
    marginBottom: spacing.sm,
  },
  zoneGroup: {
    marginBottom: spacing.md,
  },
  zoneLabel: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: spacing.xs,
    paddingHorizontal: spacing.xs,
  },
  branchRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  branchDropdownGrow: {
    flex: 1,
    minWidth: 0,
  },
  branchDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    minHeight: 44,
  },
  branchDropdownLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flex: 1,
    minWidth: 0,
  },
  branchDropdownLabel: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
    fontSize: 14,
  },
  dropdownList: {
    marginTop: 4,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    overflow: 'hidden',
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
    minHeight: 44,
  },
  dropdownItemActive: {
    backgroundColor: ticketColors.gold + '0D',
  },
  dropdownItemText: {
    ...typography.body,
    color: ticketColors.ink,
    fontSize: 14,
    flex: 1,
  },
  dropdownItemTextActive: {
    color: ticketColors.gold,
    fontWeight: '700',
  },
  removeBranchBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.bad + '30',
    backgroundColor: ticketColors.bad + '08',
    minHeight: 44,
  },
  removeBranchBtnLabel: {
    ...typography.caption,
    color: ticketColors.bad,
    fontWeight: '600',
    fontSize: 12,
  },
  presetRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  presetChip: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    minHeight: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  presetChipLabel: {
    ...typography.caption,
    color: ticketColors.ink,
    fontWeight: '700',
    fontSize: 12,
  },
  permList: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    overflow: 'hidden',
  },
  permBlock: {
    backgroundColor: ticketColors.paper,
  },
  permBlockBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
  },
  permTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: 12,
    minHeight: 48,
  },
  permModuleName: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
    fontSize: 14,
  },
  permValue: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  permValueText: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '600',
  },
  permValueTextActive: {
    color: ticketColors.gold,
    fontWeight: '700',
  },
  levelList: {
    paddingHorizontal: spacing.sm,
    paddingBottom: spacing.sm,
    gap: 4,
  },
  levelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.sm,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    minHeight: 44,
  },
  levelRowActive: {
    borderColor: ticketColors.gold + '50',
    backgroundColor: ticketColors.gold + '10',
  },
  levelRowText: {
    ...typography.body,
    color: ticketColors.ink2,
    fontSize: 14,
    fontWeight: '500',
  },
  levelRowTextActive: {
    color: ticketColors.gold,
    fontWeight: '700',
  },
  agendaManageRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginTop: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
  },
  agendaManageCopy: {
    flex: 1,
    gap: 2,
  },
  agendaManageTitle: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
    fontSize: 13,
  },
  agendaManageHint: {
    ...typography.caption,
    color: ticketColors.ink2,
    lineHeight: 16,
  },
  customizeBlock: {
    marginTop: spacing.xs,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    overflow: 'hidden',
  },
  customizeTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.sm,
    paddingVertical: 10,
    backgroundColor: ticketColors.paper2,
  },
  customizeTitle: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '700',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
    minHeight: 44,
  },
  actionLabel: {
    ...typography.body,
    color: ticketColors.ink,
    fontSize: 13,
    flex: 1,
  },
  pendingChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  addBranchChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: spacing.sm,
    paddingVertical: 5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.gold + '40',
    backgroundColor: ticketColors.gold + '10',
  },
  addBranchChipLabel: {
    ...typography.caption,
    color: ticketColors.gold,
    fontWeight: '700',
    fontSize: 12,
  },
  allBranchesHint: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
  footBtn: {
    flexGrow: 0,
  },
  footBtnGrow: {
    flexGrow: 1,
    flexBasis: 140,
  },
  revokeFootBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.bad + '30',
    backgroundColor: ticketColors.bad + '08',
    marginTop: spacing.xs,
  },
  revokeFootBtnLabel: {
    ...typography.body,
    color: ticketColors.bad,
    fontWeight: '700',
    fontSize: 14,
  },
});
